/**
 * One note card inside the per-row ``ExceptionDrawer`` thread.
 *
 * Layout (Google Sheets-style compact thread):
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ [LH] Lee Haskings [EVO_TEAM] · 20 May 2026, 08:23 PM AEST   ⋮ │
 *   │      testing a note here                                    │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * - Header row: avatar + name + role chip + middle dot + timestamp,
 *   with a hover/focus-only kebab anchored to the top-right of the
 *   card. Kebab stays at zero opacity until the row is hovered or
 *   focus lands inside, so the thread reads clean at rest.
 * - Body sits BELOW the header row aligned to the author's name (not
 *   under the avatar) via a left padding equal to avatar + gap.
 * - 14px body text, 20px line-height. 6-8px spacing between header
 *   and body. 10px padding.
 * - Inline edit replaces the body with a small TextField + Save /
 *   Cancel (no second dialog).
 * - Delete opens the shared ``ConfirmDialog`` (danger tone).
 */
import React, { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import RoleBadge from '../../shared/RoleBadge'
import ConfirmDialog from '../../shared/dialogs/ConfirmDialog'
import { poolwerxColors } from '../../../theme/colors'
import { formatRelativeTimestamp } from '../../../utils/dateFormatters'
import { initialsOf } from '../../../utils/userDisplay'
import {
  dataDomainToObjectType,
  generateExceptionUrl,
} from '../../../utils/urls'
import {
  linkifyNoteBody,
  findDisallowedLinks,
  disallowedLinksMessage,
} from '../../../utils/linkifyNoteBody'

const BODY_MAX = 2000
const AVATAR_SIZE = 24
const AVATAR_GAP = 10 // px - matches Stack spacing=1.25 (1.25 * 8 = 10)

// Shared chip styling for the muted exception-type tag. The link
// variant is used when the card belongs to a sibling exception so the
// chip itself deep-links to that thread.
const contextChipBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: '0.02em',
  bgcolor: 'rgba(15,23,42,0.05)',
  borderRadius: 0.5,
  px: 0.75,
  py: 0.125,
  lineHeight: 1.4,
  maxWidth: 220,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const

const contextChipLinkSx = {
  ...contextChipBaseSx,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': { bgcolor: 'rgba(15,23,42,0.1)' },
} as const

/**
 * Minimal shape `NoteItem` needs to render a card. Both
 * ``ExceptionNote`` (current thread, has ``can_edit`` / ``can_delete``)
 * and ``SiblingNote`` (read-only from a sibling thread, lacks those
 * flags) satisfy it. Edit/delete controls only appear for current-source
 * cards that opt in via ``can_edit`` / ``can_delete``.
 */
export interface NoteItemNoteAuthor {
  id: number | null
  name: string | null
  email: string | null
  role: string | null
}

export interface NoteItemNote {
  id: number
  body: string
  created_at: string
  updated_at: string | null
  author: NoteItemNoteAuthor
  /** Who last edited (when a non-author edited it). Drives "edited by X". */
  edited_by?: NoteItemNoteAuthor | null
  is_own?: boolean
  can_edit?: boolean
  can_delete?: boolean
}

export interface NoteItemProps {
  note: NoteItemNote
  /** Display label for the exception-type tag (falls back to ``exceptionCode``). */
  exceptionName: string
  /** Exception code the card belongs to - used to build the sibling deep-link. */
  exceptionCode: string
  /** Backend data_domain - mapped to the inspector URL segment for the deep-link. */
  dataDomain: string
  /** True when the card belongs to the drawer's current exception. */
  isCurrent: boolean
  /** Franchise partner code - used to build the sibling deep-link. */
  fpCode: string
  /** FP DB id - emits the tenure-pinned sibling deep-link when known. */
  fpId?: number
  onEdit: (id: number, body: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
}

const NoteItem: React.FC<NoteItemProps> = ({
  note,
  exceptionName,
  exceptionCode,
  dataDomain,
  isCurrent,
  fpCode,
  fpId,
  onEdit,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.body)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const author = note.author
  // Preserve the author's line breaks, but collapse any run of 2+ blank lines
  // down to a single blank line so a note padded with whitespace doesn't blow
  // out the row height.
  const displayBody = note.body.replace(/\n{3,}/g, '\n\n')
  const { label: created, title: createdTooltip, dateTime: createdISO } =
    formatRelativeTimestamp(note.created_at)
  const wasEdited = note.updated_at !== null
  // When a non-author edited the note, name them; otherwise a bare
  // "(edited)" marker. Editor name falls back to email.
  const editorName = note.edited_by?.name || note.edited_by?.email || null
  const editedSuffix = wasEdited
    ? editorName
      ? ` (edited by ${editorName})`
      : ' (edited)'
    : ''
  // Sibling cards are always read-only. Even if a future API change
  // starts returning can_edit/can_delete on the siblings endpoint, the
  // inline-edit path stays locked behind isCurrent.
  const canAct = isCurrent && (note.can_edit || note.can_delete)
  const menuOpen = Boolean(anchorEl)

  const contextLabel = exceptionName || exceptionCode
  const objectType = dataDomainToObjectType(dataDomain)
  const siblingHref =
    !isCurrent && objectType
      ? generateExceptionUrl(fpCode, objectType, exceptionCode, fpId)
      : null

  const startEdit = () => {
    setAnchorEl(null)
    setDraft(note.body)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraft(note.body)
  }

  // Block saving an edit that introduces a link to a non-allowlisted domain
  // (the backend rejects it too via validate_note_links).
  const editLinkError = editing
    ? disallowedLinksMessage(findDisallowedLinks(draft))
    : null

  const saveEdit = async () => {
    const trimmed = draft.trim()
    if (!trimmed || trimmed.length > BODY_MAX || editLinkError) return
    setSaving(true)
    try {
      await onEdit(note.id, trimmed)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const openDeleteConfirm = () => {
    setAnchorEl(null)
    setConfirmOpen(true)
  }

  const confirmDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(note.id)
      setConfirmOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  // Body indentation: avatar width + the gap between avatar and the
  // header content. Aligns body text with the author's name rather
  // than under the avatar.
  const bodyIndentPx = AVATAR_SIZE + AVATAR_GAP

  return (
    <Box
      sx={{
        position: 'relative',
        // Borderless, low-chrome row - the thread reads as one quiet list
        // (dividers between rows come from the parent Stack), not a stack of
        // cards. Compact padding keeps the expanded view light.
        px: 0.75,
        // A little more vertical room so the dividers between notes breathe.
        py: 1.125,
        borderRadius: 1,
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'rgba(15,23,42,0.03)' },
        // Reveal the kebab on hover/focus only - keeps the thread
        // visually quiet at rest while staying keyboard-reachable.
        '&:hover .note-kebab, &:focus-within .note-kebab': {
          opacity: 1,
        },
        // When the menu is open, force the kebab visible too so it
        // doesn't disappear mid-interaction.
        ...(menuOpen ? { '& .note-kebab': { opacity: 1 } } : {}),
      }}
    >
      {/* Header: avatar floats left; name + role chip + timestamp
       * flow as inline-block items inside the content column to its
       * right. When the line wraps, the timestamp drops directly
       * under the name (the avatar is no longer a flex sibling). No
       * separator dot - the spacing between role chip and timestamp
       * is enough at desktop widths, and a dangling dot at the end
       * of the previous line on narrow widths is the exact orphan
       * the design called out.
       *
       * Kebab is absolutely positioned top-right so it never affects
       * the header's sizing - no layout shift when it appears. */}
      <Box
        sx={{
          // Reserve right-edge space for the top-right timestamp (and, on
          // actionable cards, the hover kebab that sits just right of it) so
          // a long name wraps on the left instead of running under either.
          pr: canAct ? '84px' : '60px',
          position: 'relative',
          minHeight: AVATAR_SIZE,
          // Vertically centre the header content against the avatar so the
          // name, role chip and timestamp line up with the initials.
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Avatar
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: '0.65rem',
            bgcolor: poolwerxColors.teal,
          }}
        >
          {initialsOf(author.name, author.email)}
        </Avatar>
        <Box
          sx={{
            pl: `${AVATAR_SIZE + AVATAR_GAP}px`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 0.875,
            rowGap: 0.25,
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '0.875rem' }}
          >
            {author.name ||
              author.email ||
              (author.id != null ? `User ${author.id}` : 'Unknown user')}
          </Typography>
          <RoleBadge user={author} quiet />
        </Box>
        {/* Timestamp pinned to the top-right of the header row. Absolutely
          * positioned so it never wraps below the name on narrow widths;
          * sits clear of (left of) the hover kebab via the right offset. */}
        <Typography
          component="time"
          dateTime={createdISO}
          title={createdTooltip}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: canAct ? 28 : 6,
            fontSize: '12px',
            lineHeight: 1.3,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            cursor: 'help',
          }}
        >
          {created}
          {editedSuffix}
        </Typography>
      </Box>

      {canAct && !editing && (
        <IconButton
          size="small"
          aria-label="Note actions"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          className="note-kebab"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            p: 0.25,
            opacity: 0,
            // Muted colour so the icon stays quiet even while
            // visible on hover/focus - the note body is the
            // primary content, the menu is secondary.
            color: 'text.disabled',
            transition: 'opacity 120ms ease-out',
            '&:hover': { color: 'text.secondary' },
            '&:focus-visible': { opacity: 1 },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Layer the menu above the parent panel (which sits at
        // ``theme.zIndex.modal + 10``). Without this the popover
        // portals to document.body but renders behind the drawer.
        slotProps={{
          paper: {
            sx: (theme) => ({
              zIndex: theme.zIndex.modal + 20,
            }),
          },
          root: {
            sx: (theme) => ({
              zIndex: theme.zIndex.modal + 20,
            }),
          },
        }}
      >
        {note.can_edit && (
          <MenuItem onClick={startEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {note.can_delete && (
          <MenuItem onClick={openDeleteConfirm}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        tone="danger"
        title="Delete note"
        confirmLabel="Delete"
        pending={deleting}
        body={<>This permanently removes the note.</>}
      />


      {/* Exception-type tag - only on SIBLING cards, where it names (and
       * deep-links to) the other thread the note came from. Current-source
       * notes belong to the exception the drawer already shows, so the tag
       * would just duplicate that context - omitted to keep the row light. */}
      {!editing && !isCurrent && siblingHref && (
        <Box
          sx={{
            pl: `${bodyIndentPx}px`,
            mt: 0.5,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <Typography
            component={RouterLink}
            to={siblingHref}
            sx={contextChipLinkSx}
            title={contextLabel}
          >
            {contextLabel}
          </Typography>
        </Box>
      )}

      {/* Body sits below the header row, indented to align with the
       * author name rather than the avatar. 6px space above keeps the
       * header + body visually paired without inflating the card. */}
      {editing ? (
        <Box sx={{ mt: 0.75, pl: `${bodyIndentPx}px` }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="small"
            error={Boolean(editLinkError)}
            inputProps={{ 'aria-label': 'Edit note body' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '14px',
                lineHeight: '19px',
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '14px',
                lineHeight: '19px',
              },
            }}
          />
          {editLinkError && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, display: 'block', color: 'error.main', fontSize: '0.7rem' }}
            >
              {editLinkError}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 0.75 }}
          >
            <Button
              size="small"
              onClick={cancelEdit}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveEdit}
              disabled={!draft.trim() || draft.length > BODY_MAX || saving || Boolean(editLinkError)}
              sx={{
                bgcolor: poolwerxColors.teal,
                '&:hover': { bgcolor: poolwerxColors.tealHover },
                textTransform: 'none',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Typography
          sx={{
            // Compact gap below the author row; the body is the primary
            // content but the row stays tight.
            mt: 0.5,
            pl: `${bodyIndentPx}px`,
            whiteSpace: 'pre-wrap',
            fontSize: '14px',
            lineHeight: 1.45,
            color: 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          {linkifyNoteBody(displayBody)}
        </Typography>
      )}
    </Box>
  )
}

export default NoteItem
